import { useEffect, useState, type MouseEvent } from "react"
import { motion, useMotionValue, useSpring, useMotionTemplate } from "framer-motion"
import RankCard from "./RankCard"
import type { RankingData } from "../types"

interface RankCard3DProps {
  data: RankingData
}

function RankCard3D({ data }: RankCard3DProps) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  )
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  const px = useMotionValue(0)
  const py = useMotionValue(0)
  const rotateY = useSpring(px, { stiffness: 150, damping: 18 })
  const rotateX = useSpring(py, { stiffness: 150, damping: 18 })

  const angle = useMotionValue(115)
  const sheenX = useMotionValue(50)
  const sheenY = useMotionValue(50)
  const spotRaw = useMotionValue(0)
  const spotOpacity = useSpring(spotRaw, { stiffness: 150, damping: 20 })
  const bgPos = useMotionTemplate`${sheenX}% ${sheenY}%`
  const bgImage = useMotionTemplate`linear-gradient(${angle}deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)`

  function updatePosition(e: MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    px.set((x / rect.width - 0.5) * 16)
    py.set(-(y / rect.height - 0.5) * 16)
    sheenX.set((x / rect.width) * 100)
    sheenY.set((y / rect.height) * 100)
    angle.set(70 + (x / rect.width) * 90)
  }

  function handleMouseEnter(e: MouseEvent<HTMLDivElement>) {
    updatePosition(e)
    spotRaw.set(1)
  }

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    updatePosition(e)
  }

  function handleMouseLeave() {
    spotRaw.set(0)
    px.set(0)
    py.set(0)
  }

  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mx-auto w-fit"
      >
        <RankCard data={data} />
      </motion.div>
    )
  }

  return (
    <div style={{ perspective: 1000 }}>
      <motion.div
        initial={{ rotateY: -90, opacity: 0, scale: 0.85 }}
        animate={{ rotateY: 0, opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        style={{ transformStyle: "preserve-3d" }}
      >
        <motion.div
          className="relative mx-auto w-fit"
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <RankCard data={data} />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-2xl"
            style={{
              backgroundImage: bgImage,
              backgroundSize: "250% 250%",
              backgroundPosition: bgPos,
              opacity: spotOpacity,
            }}
          />
        </motion.div>
      </motion.div>
    </div>
  )
}

export default RankCard3D
